import { FormEventHandler } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { Toast } from "@sscs/ui";
import { GroupRole } from "Types";
import { apiErrorToast } from "Utils";
import * as yup from "Utils/yup";
import { useUpdateUserGroupRoleMutation } from "../../services/users.api";

type FormData = {
  role: GroupRole;
};

type UseEditGroupRoleFormProps = {
  onClose: () => void;
  currentRole: GroupRole;
  memberId: string;
  groupId: string;
};

type UseEditGroupRoleForm = {
  errors: FieldErrors<FormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  methods: UseFormReturn<FormData>;
};

const EditGroupRoleFormSchema: yup.SchemaOf<FormData> = yup.object({
  role: yup.mixed<GroupRole>().required(),
});

export const useEditGroupRoleForm = ({
  onClose,
  currentRole,
  memberId,
  groupId,
}: UseEditGroupRoleFormProps): UseEditGroupRoleForm => {
  const [updateRole] = useUpdateUserGroupRoleMutation();

  const methods = useForm<FormData>({
    resolver: yupResolver(EditGroupRoleFormSchema),
    defaultValues: { role: currentRole },
  });

  const {
    formState: { errors, isDirty },
    handleSubmit,
  } = methods;

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (!isDirty) {
      onClose();
      return;
    }

    try {
      await updateRole({
        userId: memberId,
        body: {
          group_id: groupId,
          is_assigned: true,
          role: data.role,
        },
      }).unwrap();

      Toast.show({
        title: "Changed member's group role",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not not change member's group role");
    }

    onClose();
  };

  return {
    onSubmit: handleSubmit(onSubmit),
    errors,
    methods,
  };
};
